import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  SelectChangeEvent
} from '@mui/material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { User } from '../../models/types';
import { getRoles } from '../../firebase/services/roleService';
import { Role } from '../../types/rbac';
import { createUser } from '../../firebase/services/userService';

const StaffEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [staffMember, setStaffMember] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [password, setPassword] = useState('');

  const isNewStaff = !id;

  useEffect(() => {
    const fetchStaffMember = async () => {
      try {
        if (!id) {
          // For new staff, initialize with empty values
          setStaffMember({
            id: '',
            email: '',
            firstName: '',
            lastName: '',
            phone: '',
            roleId: '',
            passcode: '',
            createdAt: new Date(),
            updatedAt: new Date()
          });
          setLoading(false);
          return;
        }
        
        const docRef = doc(db, 'users', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setStaffMember(docSnap.data() as User);
        } else {
          setError('Staff member not found');
        }
      } catch (err) {
        console.error('Error fetching staff member:', err);
        setError('Failed to load staff member data');
      } finally {
        setLoading(false);
      }
    };

    fetchStaffMember();
  }, [id]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const fetchedRoles = await getRoles();
        setRoles(fetchedRoles);
      } catch (err) {
        console.error('Error fetching roles:', err);
        setError('Failed to load roles');
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setStaffMember(prev => prev ? { ...prev, [name]: value } : null);
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setStaffMember(prev => prev ? { ...prev, [name]: value } : null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!staffMember) return;

    setLoading(true);
    try {
      if (isNewStaff) {
        // Create new staff member
        if (!password) {
          throw new Error('Password is required for new staff members');
        }
        await createUser(
          staffMember.email,
          password,
          staffMember.firstName,
          staffMember.lastName,
          staffMember.phone,
          staffMember.roleId,
          staffMember.passcode
        );
      } else {
        // Update existing staff member
        const docRef = doc(db, 'users', id!);
        await updateDoc(docRef, {
          ...staffMember,
          updatedAt: new Date()
        });
      }
      setSuccess(true);
      setTimeout(() => navigate('/admin/business/staff'), 2000);
    } catch (err) {
      console.error('Error saving staff member:', err);
      setError(err instanceof Error ? err.message : 'Failed to save staff member');
    } finally {
      setLoading(false);
    }
  };

  if (loading || rolesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {isNewStaff ? 'Add New Staff Member' : 'Edit Staff Member'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Staff member {isNewStaff ? 'created' : 'updated'} successfully
          </Alert>
        )}

        {staffMember ? (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={staffMember.firstName}
                  onChange={handleTextChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={staffMember.lastName}
                  onChange={handleTextChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={staffMember.email}
                  onChange={handleTextChange}
                  required
                />
              </Grid>
              {isNewStaff && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={staffMember.phone}
                  onChange={handleTextChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="roleId"
                    value={staffMember.roleId || ''}
                    label="Role"
                    onChange={handleSelectChange}
                    required
                  >
                    {roles.map(role => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Launch Ramp Passcode"
                  name="passcode"
                  type="text"
                  value={staffMember.passcode || ''}
                  onChange={handleTextChange}
                  helperText="Staff and admin users need a passcode to access the launch ramp"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/business/staff')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    {isNewStaff ? 'Create Staff Member' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        ) : (
          <Alert severity="error">Staff member not found</Alert>
        )}
      </Paper>
    </Box>
  );
};

export default StaffEditPage; 